import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { BookingTransaction, GetBookingResponseBody } from 'app/data/types/booking';
import { useApi } from 'app/shared/contexts/api';
import { getApiLocale } from 'app/shared/hooks/intl-provider/intl-provider.hooks';
import { useOpenParams } from 'app/shared/hooks/use-open-params';

export const BOOKING_QUERY_KEY = 'booking';

export const getBookingQueryKey = (transactionUuid: string): QueryKey => [BOOKING_QUERY_KEY, transactionUuid];

interface UseBookingQueryResult {
  booking: BookingTransaction | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

/**
 * Fetch a single resource booking for the in-app detail view.
 * `GET /api/tenant/v1/users/current/transactions/{transactionUuid}` → `{ data: BookingTransaction }`.
 */
export const useBookingQuery = (transactionUuid: string): UseBookingQueryResult => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const { locale } = useIntl();

  const fetchBooking = async (): Promise<BookingTransaction | null> => {
    const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
    const payload = await apiRequest<GetBookingResponseBody, void, void>({
      // `lang` opts into the reader-locale resource name; without it the API returns the untranslated
      // snapshot. Sent via getApiLocale, not `locale` raw — the API needs a regional tag (see the helper).
      url: `${apiUrl}/api/tenant/v1/users/current/transactions/${encodeURIComponent(transactionUuid)}?lang=${encodeURIComponent(getApiLocale(locale))}`,
      method: HttpMethod.GET,
      headers,
    });
    return !payload.isEmpty() ? payload.data.data ?? null : null;
  };

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: [...getBookingQueryKey(transactionUuid), locale],
    queryFn: fetchBooking,
    enabled: !!apiUrl && !!buildingUuid && !!transactionUuid,
    staleTime: 30_000,
    retry: false,
  });

  return { booking: data ?? null, isLoading, isError, isSuccess };
};
